import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { galleryImages } from '../data/galleryData';
import { X } from 'lucide-react';

const Gallery = () => {
  const { t, language } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const categories = [
    { id: 'all', label: t('allCategories') },
    { id: 'politics', label: t('politics') },
    { id: 'sports', label: t('sports') },
    { id: 'nature', label: t('nature') },
    { id: 'people', label: t('people') },
    { id: 'foreignCountries', label: t('foreignCountries') },
  ];

  const filteredImages =
    selectedCategory === 'all'
      ? galleryImages
      : galleryImages.filter((img) => img.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Enhanced Hero Section */}
      <div className="relative pt-28 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-6 items-center">
            {/* Left side - Text (3 columns) */}
            <div className={`lg:col-span-3 text-center lg:text-left transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
                {t('heroTitle')}
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-3 font-medium">
                {t('heroSubtitle')}
              </p>
              <p className="text-base md:text-lg text-gray-500 mb-6">
                {t('heroDescription')}
              </p>
              
              {/* Stats */}
              <div className="grid grid-cols-2 gap-6 max-w-sm mx-auto lg:mx-0">
                <div className="text-center lg:text-left">
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">40+</div>
                  <div className="text-sm text-gray-600">
                    {language === 'fi' ? 'Vuotta kokemusta' : language === 'en' ? 'Years Experience' : 'År erfarenhet'}
                  </div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">120k+</div>
                  <div className="text-sm text-gray-600">
                    {language === 'fi' ? 'Arkistoituja kuvia' : language === 'en' ? 'Archived images' : 'Arkiverade bilder'}
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Portrait (2 columns) */}
            <div className={`lg:col-span-2 relative transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500 max-w-sm mx-auto lg:max-w-none lg:ml-auto lg:mr-0">
                <img
                  src="https://customer-assets.emergentagent.com/job_nukari-photography/artifacts/ok7c2sby_Jussi-Nukari-kameroineen-eduskuntatalon-pater-noster-hississa.avif"
                  alt="Jussi Nukari"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="sticky top-20 z-40 bg-white/95 backdrop-blur-sm shadow-sm py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 transform hover:scale-105 ${
                  selectedCategory === category.id
                    ? 'bg-gray-900 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Masonry Gallery */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
          {filteredImages.map((image, index) => {
            // Get category label in current language
            const categoryLabel = categories.find(cat => cat.id === image.category)?.label || '';
            
            return (
              <div
                key={image.id}
                className={`break-inside-avoid group cursor-pointer transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 50}ms` }}
                onClick={() => setSelectedImage(image)}
              >
                <div className="relative overflow-hidden rounded-lg shadow-md hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
                  <img
                    src={image.url}
                    alt={categoryLabel}
                    className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p className="text-white text-sm font-medium transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                        {categoryLabel}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (() => {
        const categoryLabel = categories.find(cat => cat.id === selectedImage.category)?.label || '';
        return (
          <div
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 animate-in fade-in duration-300"
            onClick={() => setSelectedImage(null)}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            >
              <X className="w-8 h-8" />
            </button>
            <div
              className="relative max-w-7xl max-h-[90vh] animate-in zoom-in-95 duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.url}
                alt={categoryLabel}
                className="max-w-full max-h-[90vh] object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white text-lg font-medium">{categoryLabel}</p>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Gallery;